'use client';
import React, { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import {
  Flame, Zap, Activity, ChevronRight, ChevronLeft,
  User, Info, Loader2, CheckCircle2, AlertCircle
} from 'lucide-react';
import { scoreEngine, scoreIgnition, scoreBounce, calculateEPS } from '@/lib/scoring';
import { createClient } from '@/lib/supabase/client';

interface AthleteOption {
  id: string;
  name: string;
  sport: string;
  position: string;
  heightIn: number;
  weightLbs: number;
  trainingAge: number;
  injuryHistory: string;
  seasonPhase: string;
  avatarInitials: string;
}

interface AssessmentFormProps {
  athletes: AthleteOption[];
}

interface FormValues {
  athleteId: string;
  sessionDate: string;
  // Engine
  trapBarDL: string;
  backSquat: string;
  frontSquat: string;
  splitSquat: string;
  bodyweight: string;
  // Ignition
  cmjHeight: string;
  sqjHeight: string;
  sprint10yd: string;
  sprint20yd: string;
  medBallThrow: string;
  // Bounce
  rsi: string;
  dropJumpHeight: string;
  broadJump: string;
  tripleJump: string;
  pogoHeight: string;
}

const TABS = [
  { id: 'tab-engine', key: 'engine', label: 'Engine', icon: Flame, color: '#EF4444', desc: 'Max Strength / Force Production' },
  { id: 'tab-ignition', key: 'ignition', label: 'Ignition', icon: Zap, color: '#F59E0B', desc: 'Rate of Force Development' },
  { id: 'tab-bounce', key: 'bounce', label: 'Bounce', icon: Activity, color: '#10B981', desc: 'Elastic + Reactive Power' },
];

function ScorePreviewBar({ score, label, color }: { score: number; label: string; color: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-zinc-500 w-16 font-medium">{label}</span>
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="font-mono tabular-nums text-xs font-semibold w-8 text-right" style={{ color }}>{score}</span>
    </div>
  );
}

function FieldRow({ label, name, unit, placeholder, hint, register, error, type = 'number' }: any) {
  return (
    <div>
      <label className="label">{label} <span className="text-zinc-600 normal-case tracking-normal font-normal">({unit})</span></label>
      {hint && <p className="text-xs text-zinc-600 mb-1.5">{hint}</p>}
      <input
        {...register(name, {
          required: 'Required',
          min: { value: 0.01, message: 'Must be > 0' },
        })}
        type={type}
        step="any"
        placeholder={placeholder}
        className="input-field"
      />
      {error && <p className="text-xs text-red-400 mt-1">{error.message}</p>}
    </div>
  );
}

export default function AssessmentForm({ athletes }: AssessmentFormProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const router = useRouter();

  const { register, watch, handleSubmit, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      athleteId: athletes[0]?.id ?? '',
      sessionDate: new Date().toISOString().split('T')[0],
    },
  });

  const vals = watch();

  const engineScore = useCallback(() => {
    const tb = parseFloat(vals.trapBarDL);
    const bs = parseFloat(vals.backSquat);
    const fs = parseFloat(vals.frontSquat);
    const ss = parseFloat(vals.splitSquat);
    const bw = parseFloat(vals.bodyweight);
    if (!tb || !bs || !fs || !ss || !bw) return 0;
    return scoreEngine({ trapBarDL: tb, backSquat: bs, frontSquat: fs, splitSquat: ss, bodyweight: bw });
  }, [vals.trapBarDL, vals.backSquat, vals.frontSquat, vals.splitSquat, vals.bodyweight]);

  const ignitionScore = useCallback(() => {
    const cmj = parseFloat(vals.cmjHeight);
    const sqj = parseFloat(vals.sqjHeight);
    const s10 = parseFloat(vals.sprint10yd);
    const s20 = parseFloat(vals.sprint20yd);
    const mb = parseFloat(vals.medBallThrow);
    if (!cmj || !sqj || !s10 || !s20 || !mb) return 0;
    return scoreIgnition({ cmjHeight: cmj, sqjHeight: sqj, sprint10yd: s10, sprint20yd: s20, medBallThrow: mb });
  }, [vals.cmjHeight, vals.sqjHeight, vals.sprint10yd, vals.sprint20yd, vals.medBallThrow]);

  const bounceScore = useCallback(() => {
    let rsi = parseFloat(vals.rsi);
    const dj = parseFloat(vals.dropJumpHeight);
    const bj = parseFloat(vals.broadJump);
    const tj = parseFloat(vals.tripleJump);
    const pg = parseFloat(vals.pogoHeight);
    if (!rsi || !dj || !bj || !tj || !pg) return 0;
    return scoreBounce({ rsi, dropJumpHeight: dj, broadJump: bj, tripleJump: tj, pogoHeight: pg });
  }, [vals.rsi, vals.dropJumpHeight, vals.broadJump, vals.tripleJump, vals.pogoHeight]);

  const eng = engineScore();
  const ign = ignitionScore();
  const bnc = bounceScore();
  const eps = calculateEPS(eng, ign, bnc);

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const supabase = createClient();

      // 1. Get authenticated user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('You must be logged in to save an assessment.');
      }

      // 2. Validate selected athlete
      const selectedAthleteId = data.athleteId;
      if (!selectedAthleteId) {
        throw new Error('Missing athlete — please select an athlete before saving.');
      }

      // 3. Fetch the selected athlete profile to get user_id and verify access
      const { data: athleteProfile, error: profileError } = await supabase
        .from('athlete_profiles')
        .select('id, user_id, name, pr_squat, pr_deadlift, pr_cmj, pr_sprint_10yd, pr_broad_jump')
        .eq('id', selectedAthleteId)
        .single();

      if (profileError || !athleteProfile) {
        throw new Error('Athlete not found. Please refresh and try again.');
      }

      // 4. Determine if this is a coach submitting for an athlete or athlete submitting own data
      const isOwnProfile = athleteProfile.user_id === user.id;
      let createdBy: string = user.id;

      if (!isOwnProfile) {
        // Coach path: verify the coach is assigned to this athlete
        const { data: coachLink, error: coachLinkError } = await supabase
          .from('coach_athletes')
          .select('id')
          .eq('coach_id', user.id)
          .eq('athlete_id', selectedAthleteId)
          .eq('status', 'active')
          .single();

        if (coachLinkError || !coachLink) {
          throw new Error('Permission denied — you are not assigned to this athlete.');
        }
        createdBy = user.id;
      }

      // 5. Cast all numeric fields from strings to numbers
      const n = (v: string) => {
        const parsed = parseFloat(v);
        if (isNaN(parsed)) throw new Error(`Invalid number format: "${v}"`);
        return parsed;
      };

      let trapBarDL: number, backSquat: number, frontSquat: number, splitSquat: number, bodyweight: number;
      let cmjHeight: number, sqjHeight: number, sprint10yd: number, sprint20yd: number, medBallThrow: number;
      let rsi: number, dropJumpHeight: number, broadJump: number, tripleJump: number, pogoHeight: number;

      try {
        trapBarDL = n(data.trapBarDL);
        backSquat = n(data.backSquat);
        frontSquat = n(data.frontSquat);
        splitSquat = n(data.splitSquat);
        bodyweight = n(data.bodyweight);
        cmjHeight = n(data.cmjHeight);
        sqjHeight = n(data.sqjHeight);
        sprint10yd = n(data.sprint10yd);
        sprint20yd = n(data.sprint20yd);
        medBallThrow = n(data.medBallThrow);
        rsi = n(data.rsi);
        dropJumpHeight = n(data.dropJumpHeight);
        broadJump = n(data.broadJump);
        tripleJump = n(data.tripleJump);
        pogoHeight = n(data.pogoHeight);
      } catch (castErr: any) {
        throw new Error(`Invalid number format — ${castErr.message}`);
      }

      // 6. Recalculate scores server-side from cast values
      const engineSc = scoreEngine({ trapBarDL, backSquat, frontSquat, splitSquat, bodyweight });
      const ignitionSc = scoreIgnition({ cmjHeight, sqjHeight, sprint10yd, sprint20yd, medBallThrow });
      const bounceSc = scoreBounce({ rsi, dropJumpHeight, broadJump, tripleJump, pogoHeight });
      const epsSc = calculateEPS(engineSc, ignitionSc, bounceSc);

      // 7. Insert test session — user_id is the athlete's user_id for RLS compliance
      //    created_by tracks who entered the data (coach or athlete themselves)
      const athleteUserId = athleteProfile.user_id ?? user.id;

      const { data: insertedSession, error: insertError } = await supabase
        .from('test_sessions')
        .insert({
          athlete_id: selectedAthleteId,
          user_id: athleteUserId,
          created_by: createdBy,
          session_date: data.sessionDate,
          engine_score: engineSc,
          ignition_score: ignitionSc,
          bounce_score: bounceSc,
          eps: epsSc,
          trap_bar_dl: trapBarDL,
          back_squat: backSquat,
          front_squat: frontSquat,
          split_squat: splitSquat,
          bodyweight: bodyweight,
          cmj_height: cmjHeight,
          sqj_height: sqjHeight,
          sprint_10yd: sprint10yd,
          sprint_20yd: sprint20yd,
          med_ball_throw: medBallThrow,
          rsi: rsi,
          drop_jump_height: dropJumpHeight,
          broad_jump: broadJump,
          triple_jump: tripleJump,
          pogo_height: pogoHeight,
        })
        .select('id')
        .single();

      if (insertError) {
        if (insertError.code === '42501') {
          throw new Error('Permission denied — RLS policy blocked the insert. Check that you are assigned to this athlete.');
        }
        if (insertError.code === '23505') {
          throw new Error('Duplicate assessment — an assessment already exists for this athlete on this date.');
        }
        if (insertError.code === '42703') {
          throw new Error(`Schema column mismatch — ${insertError.message}`);
        }
        throw new Error(`Database insert failed — ${insertError.message}`);
      }

      // 8. Upsert athlete_eps_scores with latest scores
      const { error: epsError } = await supabase
        .from('athlete_eps_scores')
        .upsert(
          {
            athlete_id: selectedAthleteId,
            engine_score: engineSc,
            ignition_score: ignitionSc,
            bounce_score: bounceSc,
            eps_score: epsSc,
            calculated_at: new Date().toISOString(),
          },
          { onConflict: 'athlete_id' }
        );

      if (epsError && process.env.NODE_ENV === 'development') {
        console.warn('[AssessmentForm] athlete_eps_scores upsert warning:', epsError.message);
      }

      // 9. Update athlete_profiles PR columns if new PRs were set
      const prUpdates: Record<string, number> = {};
      const existingPrSquat = Number(athleteProfile.pr_squat ?? 0);
      const existingPrDeadlift = Number(athleteProfile.pr_deadlift ?? 0);
      const existingPrCmj = Number(athleteProfile.pr_cmj ?? 0);
      const existingPrSprint10 = Number(athleteProfile.pr_sprint_10yd ?? 0);
      const existingPrBroadJump = Number(athleteProfile.pr_broad_jump ?? 0);

      if (backSquat > existingPrSquat) prUpdates.pr_squat = backSquat;
      if (trapBarDL > existingPrDeadlift) prUpdates.pr_deadlift = trapBarDL;
      if (cmjHeight > existingPrCmj) prUpdates.pr_cmj = cmjHeight;
      // Sprint: lower is better — only update if existing is 0 or new is faster
      if (existingPrSprint10 === 0 || sprint10yd < existingPrSprint10) prUpdates.pr_sprint_10yd = sprint10yd;
      if (broadJump > existingPrBroadJump) prUpdates.pr_broad_jump = broadJump;

      if (Object.keys(prUpdates).length > 0) {
        const { error: prError } = await supabase
          .from('athlete_profiles')
          .update({ ...prUpdates, updated_at: new Date().toISOString() })
          .eq('id', selectedAthleteId);

        if (prError && process.env.NODE_ENV === 'development') {
          console.warn('[AssessmentForm] PR update warning:', prError.message);
        }
      }

      setSubmitSuccess(true);

      // 10. Navigate after short delay
      //     Coaches go to program builder for the selected athlete
      //     Athletes go to their own explosive profile report
      setTimeout(() => {
        if (!isOwnProfile) {
          router.push(`/training-program-builder?athleteId=${selectedAthleteId}`);
        } else {
          router.push('/explosive-profile-report');
        }
      }, 1200);

    } catch (err: any) {
      setSubmitError(err.message ?? 'An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const relStrength = vals.trapBarDL && vals.bodyweight
    ? (parseFloat(vals.trapBarDL) / parseFloat(vals.bodyweight)).toFixed(2)
    : '—';

  if (athletes.length === 0) {
    return (
      <div className="card p-8 text-center">
        <AlertCircle size={32} className="mx-auto mb-3 text-zinc-600" />
        <p className="text-zinc-400 font-medium">No athletes available</p>
        <p className="text-sm text-zinc-600 mt-1">Add an athlete to your roster before entering an assessment.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {submitError && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-red-950/40 border border-red-800/50">
          <AlertCircle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-300">{submitError}</p>
        </div>
      )}
      {submitSuccess && (
        <div className="mb-4 flex items-start gap-3 p-4 rounded-xl bg-green-950/40 border border-green-800/50">
          <CheckCircle2 size={16} className="text-green-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-300">Assessment saved — Explosive Profile generated. Redirecting...</p>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Main form area */}
        <div className="xl:col-span-2 space-y-4">
          {/* Session meta */}
          <div className="card p-5">
            <h3 className="section-header mb-4 flex items-center gap-2">
              <User size={16} style={{ color: '#E10600' }} />
              Session Info
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Athlete</label>
                <select {...register('athleteId')} className="input-field">
                  {athletes.map(a => (
                    <option key={a.id} value={a.id}>{a.name} — {a.sport}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Test Date</label>
                <input {...register('sessionDate')} type="date" className="input-field" />
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="card overflow-hidden">
            <div className="flex border-b border-zinc-800">
              {TABS.map((tab, i) => {
                const TabIcon = tab.icon;
                const isActive = activeTab === i;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(i)}
                    className={`flex-1 flex items-center justify-center gap-2 py-3.5 text-sm font-medium transition-all duration-150 border-b-2 ${
                      isActive
                        ? 'border-current text-current bg-zinc-800/50' :'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/30'
                    }`}
                    style={isActive ? { color: tab.color } : {}}
                  >
                    <TabIcon size={15} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="p-5">
              <p className="text-xs text-zinc-500 mb-5">{TABS[activeTab].desc}</p>

              {activeTab === 0 && (
                <div className="space-y-4">
                  <FieldRow label="Bodyweight" name="bodyweight" unit="lbs" placeholder="185" hint="Required for relative strength ratios" register={register} error={errors.bodyweight} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FieldRow label="Trap Bar Deadlift 1RM" name="trapBarDL" unit="lbs" placeholder="315" register={register} error={errors.trapBarDL} />
                    <FieldRow label="Back Squat 1RM" name="backSquat" unit="lbs" placeholder="275" register={register} error={errors.backSquat} />
                    <FieldRow label="Front Squat 1RM" name="frontSquat" unit="lbs" placeholder="220" register={register} error={errors.frontSquat} />
                    <FieldRow label="Split Squat (per leg)" name="splitSquat" unit="lbs" placeholder="185" hint="Best leg, loaded" register={register} error={errors.splitSquat} />
                  </div>
                  {vals.trapBarDL && vals.bodyweight && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-800 border border-zinc-700">
                      <Info size={13} className="text-zinc-500 flex-shrink-0" />
                      <p className="text-xs text-zinc-400">
                        Trap Bar Relative Strength: <span className="font-mono font-semibold text-zinc-200">{relStrength}×BW</span>
                        <span className="text-zinc-600 ml-2">(Elite target: 2.5×BW)</span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FieldRow label="Countermovement Jump" name="cmjHeight" unit="inches" placeholder="28.0" hint="With arm swing, best of 3" register={register} error={errors.cmjHeight} />
                    <FieldRow label="Squat Jump" name="sqjHeight" unit="inches" placeholder="24.0" hint="No arm swing, hands on hips" register={register} error={errors.sqjHeight} />
                    <FieldRow label="10-Yard Sprint" name="sprint10yd" unit="seconds" placeholder="1.52" hint="Electronic timing preferred" register={register} error={errors.sprint10yd} />
                    <FieldRow label="20-Yard Sprint" name="sprint20yd" unit="seconds" placeholder="2.71" hint="From standing start" register={register} error={errors.sprint20yd} />
                  </div>
                  <FieldRow label="Med Ball Throw Distance" name="medBallThrow" unit="feet" placeholder="38" hint="Overhead backward throw, 8–10lb ball, best of 3" register={register} error={errors.medBallThrow} />
                </div>
              )}

              {activeTab === 2 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FieldRow label="RSI Score" name="rsi" unit="ratio" placeholder="1.9" hint="Jump height (m) ÷ contact time (s)" register={register} error={errors.rsi} />
                    <FieldRow label="Drop Jump Height" name="dropJumpHeight" unit="inches" placeholder="22.0" hint="From 20-inch box, best of 3" register={register} error={errors.dropJumpHeight} />
                    <FieldRow label="Broad Jump" name="broadJump" unit="inches" placeholder="98" hint="Standing broad jump, best of 3" register={register} error={errors.broadJump} />
                    <FieldRow label="Triple Jump" name="tripleJump" unit="inches" placeholder="298" hint="Standing triple jump, total distance" register={register} error={errors.tripleJump} />
                  </div>
                  <FieldRow label="Pogo Jump Height" name="pogoHeight" unit="inches" placeholder="10.0" hint="Average height over 10 reps, minimal knee bend" register={register} error={errors.pogoHeight} />
                </div>
              )}

              {/* Tab nav */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-zinc-800">
                <button
                  type="button"
                  onClick={() => setActiveTab(Math.max(0, activeTab - 1))}
                  disabled={activeTab === 0}
                  className="btn-ghost disabled:opacity-30"
                >
                  <ChevronLeft size={14} /> Back
                </button>
                {activeTab < 2 ? (
                  <button
                    type="button"
                    onClick={() => setActiveTab(activeTab + 1)}
                    className="btn-primary"
                  >
                    Next: {TABS[activeTab + 1].label} <ChevronRight size={14} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting || submitSuccess}
                    className="btn-primary min-w-[180px] justify-center"
                  >
                    {isSubmitting ? (
                      <><Loader2 size={14} className="animate-spin" /> Saving Assessment...</>
                    ) : submitSuccess ? (
                      <><CheckCircle2 size={14} /> Saved!</>
                    ) : (
                      <><CheckCircle2 size={14} /> Save Assessment</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Live Score Preview */}
        <div className="space-y-4">
          <div className="card p-5 sticky top-6">
            <h3 className="section-header mb-1">Live Score Preview</h3>
            <p className="text-xs text-zinc-500 mb-5">Updates as you enter data</p>

            {/* EPS */}
            <div className="mb-5 p-4 rounded-xl" style={{ background: 'rgba(20,20,20,0.85)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">EPS</p>
              <div className="flex items-end gap-2">
                <span className="font-mono tabular-nums font-bold text-4xl" style={{ color: eps > 0 ? '#E10600' : '#3f3f46' }}>
                  {eps}
                </span>
                <span className="text-zinc-600 mb-1">/100</span>
              </div>
              <div className="h-1 rounded-full mt-2 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${eps}%`, background: 'linear-gradient(90deg, #8B0000 0%, #E10600 100%)' }}
                />
              </div>
            </div>

            <div className="space-y-3 mb-5">
              <ScorePreviewBar score={eng} label="Engine" color="#EF4444" />
              <ScorePreviewBar score={ign} label="Ignition" color="#F59E0B" />
              <ScorePreviewBar score={bnc} label="Bounce" color="#10B981" />
            </div>

            {/* Standards reference */}
            <div className="border-t border-zinc-800 pt-4">
              <p className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-3">Standards Reference</p>
              <div className="space-y-1.5">
                {[
                  { label: 'Elite', range: '90–100', color: '#FF3B30' },
                  { label: 'High Level', range: '80–89', color: '#FF6B35' },
                  { label: 'Solid', range: '70–79', color: '#10B981' },
                  { label: 'Developing', range: '60–69', color: '#F59E0B' },
                  { label: 'Major Limit.', range: '<60', color: '#B3B3B3' },
                ].map(b => (
                  <div key={`ref-${b.label}`} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: b.color }} />
                      <span className="text-zinc-400">{b.label}</span>
                    </div>
                    <span className="font-mono text-zinc-500">{b.range}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}